// noinspection DuplicatedCode
import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "react-datepicker/dist/react-datepicker.css";
import { Colors } from "../../../assets/Colors";
import { Card } from "@material-tailwind/react";
import { HrdInputGenerator } from "../../../components/form/HrdInputGenerator";
import { useForm } from "../../../components/isRequired";
import { useQuery } from "@tanstack/react-query";
import { fetchGet, fetchPost } from "../../../fetchers";
import ModalBayar from "../../../components/form/ModalBayar";
import { toast } from "react-toastify";
import { maxPageGlobal } from "../../../assets/config";
import DataTable from "react-data-table-component";
import Select from "react-select";
import debounce from "lodash/debounce";
import { BiSearch, BiRefresh, BiFilter } from "react-icons/bi";
import { BsFileEarmarkExcelFill, BsFileEarmarkPdfFill } from "react-icons/bs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
// Export Excel memakai downloadExcel dari react-export-table-to-excel — library
// ini di-bundle lewat npm (sama seperti halaman Buku Bank), jadi TIDAK perlu
// memuat SheetJS dari CDN yang dulu sering gagal di jaringan internal.
import { downloadExcel } from "react-export-table-to-excel";
import Skeltons from "../../../components/loading/Skeltons";
import { useUserBisnisUnit } from "../../../hooks/useUserBisnisUnit";

// Semua divisi = string kosong; backend memperlakukan divisi='' sebagai "tanpa
// filter". Dipakai juga sbg nilai awal agar halaman terbuka menampilkan semuanya.
const SEMUA_DIVISI = { value: "", label: "Semua Divisi" };

// User SO hanya boleh melihat data unitnya sendiri — filter Bisnis Unit dikunci
// ke SO sehingga data HO tak pernah diminta ke API (sama spt report Non PO).
const SO_UNIT = "U0002";

// Kelas tunggal untuk SEMUA tombol aksi di baris filter. Tinggi dipatok (h-10)
// agar keempatnya benar-benar sejajar — dulu tiap tombol punya padding sendiri
// sehingga tingginya beda-beda.
const BTN_BASE =
  "h-10 px-3 text-sm font-medium rounded-lg inline-flex items-center justify-center gap-1.5 whitespace-nowrap transition-colors disabled:opacity-50 disabled:cursor-not-allowed";

// Nama bulan untuk label periode di header laporan (gaya Buku Bank).
const NAMA_BULAN = [
  "", "Januari", "Februari", "Maret", "April", "Mei", "Juni",
  "Juli", "Agustus", "September", "Oktober", "November", "Desember",
];

// Header kolom untuk export Excel/PDF — satu sumber dipakai keduanya.
const EXPORT_HEADER = [
  "No", "No. Voucher", "No. PO", "Tanggal", "Keterangan", "Dibayar Kepada", "Nominal PO",
  "Nominal Bayar", "Uang Muka", "Sisa Bayar", "Status",
];

// Dua tampilan yang bisa dipilih lewat radio button:
//   pembayaran = tabel Pembayaran AP (voucher) — tampilan asli/utama.
//   hutang     = Daftar Hutang: ringkasan per PURCHASE ORDER (hanya Hutang PO) yang
//                masih BELUM LUNAS (Sisa = Total Hutang − Terbayar > 0).
const VIEW_PEMBAYARAN = "pembayaran";
const VIEW_HUTANG = "hutang";

// Header kolom export untuk tabel Daftar Hutang (per PO, belum lunas).
const EXPORT_HEADER_HUTANG = [
  "No", "No. PO", "Supplier / Keterangan", "Total Hutang", "Terbayar",
  "Sisa (Belum Lunas)",
];

// Bangun body export tabel Daftar Hutang: satu baris per PO + baris TOTAL.
const buildExportBodyHutang = (rows) => {
  let totalHutang = 0;
  let totalTerbayar = 0;
  let totalSisa = 0;
  const body = rows.map((item, i) => {
    const th = parseFloat(item.TotalHutang) || 0;
    const tb = parseFloat(item.Terbayar) || 0;
    const ss = parseFloat(item.Sisa) || 0;
    totalHutang += th;
    totalTerbayar += tb;
    totalSisa += ss;
    return [
      i + 1,
      item.PurchaseOrderID || "-",
      item.DibayarKepada || item.Keterangan || "-",
      fmtNominal(th),
      fmtNominal(tb),
      fmtNominal(ss),
    ];
  });
  body.push([
    "", "", "TOTAL", fmtNominal(totalHutang), fmtNominal(totalTerbayar),
    fmtNominal(totalSisa),
  ]);
  return body;
};

// ── Status ───────────────────────────────────────────────────
// Backend mengirim Status sbg LABEL (lihat labelStatusPembayaranAP di
// models/accounting/pembayaranap.go), bukan angka. Kunci dibuat lowercase agar
// tahan beda kapitalisasi. Hanya "Approved" yang boleh dicetak blankonya.
const STATUS_MAP = {
  approved: { value: "Approved", isApproved: true, isRejected: false },
  "menunggu approval": { value: "Menunggu Approval", isApproved: false, isRejected: false },
  "menunggu dikirim": { value: "Menunggu Dikirim", isApproved: false, isRejected: false },
  revisi: { value: "Revisi", isApproved: false, isRejected: true },
  ditolak: { value: "Ditolak", isApproved: false, isRejected: true },
};

const mapStatus = (status) => {
  if (!status) return { value: "-", isApproved: false, isRejected: false };
  return (
    STATUS_MAP[status.toString().toLowerCase()] || {
      value: status,
      isApproved: false,
      isRejected: false,
    }
  );
};

const statusClass = (st) => {
  const base = "px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap";
  if (st.isApproved) return `${base} bg-green-100 text-green-700`;
  if (st.isRejected) return `${base} bg-red-100 text-red-700`;
  return `${base} bg-yellow-100 text-yellow-700`;
};

// Nominal untuk file export: bilangan bulat berpemisah ribuan tanpa "Rp",
// supaya mudah dijumlah di Excel (mengikuti gaya Buku Bank).
const fmtNominal = (val) =>
  Math.round(parseFloat(val || 0)).toLocaleString("id-ID");

const fmtTanggal = (val) => {
  if (!val) return "-";
  const d = new Date(val);
  return isNaN(d.getTime()) ? String(val) : d.toLocaleDateString("id-ID");
};

// Bangun body export dari baris yang SEDANG TERMUAT di tabel (satu halaman),
// Sisa Bayar = nominal yang BELUM keluar sebagai kas untuk voucher ini.
//
// Voucher yang belum Approved belum mengeluarkan kas sama sekali, jadi sisanya =
// Nominal Bayar seutuhnya. Sebelum 2026-07-25 rumus "Nominal PO − Nominal Bayar
// − Uang Muka" dipakai untuk SEMUA status, sehingga voucher "Menunggu Approval"
// (yang Nominal Bayar-nya sama dengan Nominal PO) tampil Sisa Bayar Rp 0 —
// terbaca seolah sudah lunas padahal belum dibayar sama sekali.
//
// Setelah Approved barulah pembayaran dianggap terjadi, dan sisanya dihitung
// dari selisihnya. Dua hal yang membuat clamp ≥ 0 perlu: `NilaiBayar` bisa
// LEBIH BESAR dari `Nominal` (PPN ikut di nilai bayar), dan termin yang ditutup
// uang muka menyimpan `NilaiBayar = 0` dengan `DownpaymentAmount` penuh.
const hitungSisaBayar = (row) => {
  const nominalBayar = parseFloat(row.NilaiBayar) || 0;
  if (!mapStatus(row.Status).isApproved) return Math.max(0, nominalBayar);

  const nominalPO = parseFloat(row.Nominal) || 0;
  const uangMuka = parseFloat(row.DownpaymentAmount) || 0;
  return Math.max(0, nominalPO - nominalBayar - uangMuka);
};

// ditutup baris TOTAL. Dipakai bersama oleh Excel & PDF.
const buildExportBody = (rows) => {
  let totalPO = 0;
  let totalBayar = 0;
  let totalDP = 0;
  let totalSisa = 0;
  const body = rows.map((item, i) => {
    const nominalPO = parseFloat(item.Nominal) || 0;
    const nominalBayar = parseFloat(item.NilaiBayar) || 0;
    const uangMuka = parseFloat(item.DownpaymentAmount) || 0;
    const sisa = hitungSisaBayar(item);
    totalPO += nominalPO;
    totalBayar += nominalBayar;
    totalDP += uangMuka;
    totalSisa += sisa;
    return [
      i + 1,
      item.PembayaranAPID || "-",
      item.PurchaseOrderID || "-",
      fmtTanggal(item.Tanggal),
      item.Keterangan || "-",
      item.DibayarKepada || "-",
      fmtNominal(nominalPO),
      fmtNominal(nominalBayar),
      fmtNominal(uangMuka),
      fmtNominal(sisa),
      mapStatus(item.Status).value,
    ];
  });
  body.push([
    "", "", "", "", "", "TOTAL", fmtNominal(totalPO), fmtNominal(totalBayar),
    fmtNominal(totalDP), fmtNominal(totalSisa), "",
  ]);
  return body;
};

const MonitoringAP = () => {
  const navigate = useNavigate();

  // State management - Mengikuti pola MonitoringNonPO
  const [searchInput, setSearchInput] = useState(""); // Untuk input field (langsung update)
  const [searchQuery, setSearchQuery] = useState(""); // Untuk query ke API (debounced)
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(maxPageGlobal);
  const [bisnisUnit, setBisnisUnit] = useState("U0002");
  const [periode, setPeriode] = useState({
    bulan: (new Date().getMonth() + 1).toString(),
    tahun: new Date().getFullYear().toString(),
  });
  // Filter rentang tanggal. Kosong = pakai filter bulan/tahun seperti biasa;
  // begitu salah satunya diisi, backend MENGABAIKAN bulan/tahun karena rentang
  // tanggal memang sering melintasi bulan.
  const [tanggal, setTanggal] = useState({ mulai: "", akhir: "" });
  const [isExporting, setIsExporting] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  // Peran unit user login. Selama BU belum ter-resolve (sesi lama → fetch by Nik),
  // filter dikunci dulu; dibuka kembali kalau ternyata user bukan SO.
  const { bisnisUnitID: userBisnisUnit, resolved: buResolved } =
    useUserBisnisUnit();
  const isSO = userBisnisUnit === SO_UNIT;
  const lockBU = isSO || !buResolved;
  const [isSearching, setIsSearching] = useState(false);
  // Divisi (BagianID) — dikirim ke API sbg query param `divisi`.
  const [divisi, setDivisi] = useState(SEMUA_DIVISI);
  // Tampilan aktif (radio): pembayaran (voucher AP) | hutang (daftar hutang PO).
  const [viewMode, setViewMode] = useState(VIEW_PEMBAYARAN);
  // Modal detail rincian hutang satu PO (jurnal timbul + pembayaran).
  // Modal "Bayar" — sejak jurnal terbit saat pembayaran (bukan saat approval),
  // tombol inilah yang benar-benar menerbitkan jurnal.
  const [bayarModal, setBayarModal] = useState({ show: false, id: "", nominal: "" });
  const [bayarBusy, setBayarBusy] = useState(false);

  const [detailPO, setDetailPO] = useState(null); // {PurchaseOrderID, Sisa,...} baris terpilih
  const [detailData, setDetailData] = useState(null); // {Jurnal:[], Pembayaran:[]}
  const [detailLoading, setDetailLoading] = useState(false);

  // Label periode utk header laporan & judul file export (gaya Buku Bank).
  const periodeLabel = `${NAMA_BULAN[parseInt(periode.bulan, 10)] || periode.bulan} ${periode.tahun}`;

  // Opsi divisi dari master Bagian. API sudah mengembalikan bentuk {value,label}
  // (value = BagianID), jadi bisa langsung dipakai react-select.
  const { data: bagianData } = useQuery({
    queryKey: ["masterBagianMin"],
    queryFn: () => fetchGet("getMinMasterBagianV2"),
    staleTime: 5 * 60 * 1000,
  });

  const divisiOptions = useMemo(
    () => [SEMUA_DIVISI, ...(bagianData?.data ?? [])],
    [bagianData],
  );

  // Form handling untuk filter
  const initialState = {
    Bulan: new Date(),
    BisnisUnitID: "U0002",
  };

  const validations = [];

  const { values, errors, touched, customHandler } = useForm(
    initialState,
    validations,
  );

  // Debounced search - Mengikuti pola MonitoringNonPO
  const debouncedSetSearchQuery = useMemo(
    () =>
      debounce((value) => {
        console.log("Debounced value:", value);
        setSearchQuery(value);
        setPage(1);
        setIsSearching(false);
      }, 800),
    [],
  );

  // Cleanup debounce
  useEffect(() => {
    return () => {
      debouncedSetSearchQuery.cancel();
    };
  }, [debouncedSetSearchQuery]);

  // Handler untuk search input
  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setSearchInput(value);
      
      debouncedSetSearchQuery.cancel();
      
      if (value.trim() === "") {
        setSearchQuery("");
        setPage(1);
        setIsSearching(false);
      } else {
        setIsSearching(true);
        debouncedSetSearchQuery(value);
      }
    },
    [debouncedSetSearchQuery],
  );

  // Handler untuk clear search
  const handleClearSearch = useCallback(() => {
    debouncedSetSearchQuery.cancel();
    setSearchInput("");
    setSearchQuery("");
    setPage(1);
    setIsSearching(false);

    toast.info("Pencarian dibersihkan", {
      position: "top-center",
      autoClose: 800,
    });
  }, [debouncedSetSearchQuery]);

  // Update bisnis unit ketika nilai form berubah. User SO tak bisa keluar dari
  // unitnya sendiri, apa pun isi state filter.
  useEffect(() => {
    if (values.BisnisUnitID) {
      setBisnisUnit(isSO ? SO_UNIT : values.BisnisUnitID);
      setPage(1);
    }
  }, [values.BisnisUnitID, isSO]);

  // Jaring pengaman: kembalikan nilai filter ke SO kalau sempat berubah sebelum
  // BU user ter-resolve.
  useEffect(() => {
    if (isSO && values.BisnisUnitID !== SO_UNIT) {
      customHandler(SO_UNIT, "BisnisUnitID");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSO, values.BisnisUnitID]);

  // Fetch data dengan React Query - Mengikuti pola MonitoringNonPO
  const {
    data: monitoringData,
    isLoading,
    isFetching,
    refetch,
    error,
  } = useQuery({
    queryKey: [
      "monitoringAP",
      bisnisUnit,
      periode.bulan,
      periode.tahun,
      page,
      limit,
      searchQuery,
      divisi.value,
      tanggal.mulai,
      tanggal.akhir,
    ],
    queryFn: async () => {
      const endpoint = `Accounting/Mon/PembayaranAP/${bisnisUnit}/${periode.bulan}/${periode.tahun}`;

      const params = new URLSearchParams();

      if (searchQuery && searchQuery.trim() !== "") {
        params.append("search", searchQuery.trim());
      }
      if (divisi.value) {
        params.append("divisi", divisi.value);
      }

      if (tanggal.mulai) params.append("tglMulai", tanggal.mulai);
      if (tanggal.akhir) params.append("tglAkhir", tanggal.akhir);

      params.append("page", page.toString());
      params.append("limit", limit.toString());

      const url = `${endpoint}?${params.toString()}`;
      console.log("Fetching:", url);

      const response = await fetchGet(url);

      if (isFirstLoad) {
        setIsFirstLoad(false);
      }

      return response;
    },
    keepPreviousData: true,
    staleTime: 30000,
    enabled: viewMode === VIEW_PEMBAYARAN,
  });

  // Tabel Daftar Hutang PO — hutang PO belum lunas per PurchaseOrder untuk BU +
  // periode + divisi yang sama. Dimuat hanya saat tampilan "hutang" aktif; data
  // dipaginasi & difilter (search) di sisi klien.
  const {
    data: hutangResp,
    isLoading: hutangLoading,
    isFetching: hutangFetching,
    refetch: hutangRefetch,
    error: hutangError,
  } = useQuery({
    queryKey: [
      "hutangJurnal",
      bisnisUnit,
      periode.bulan,
      periode.tahun,
      divisi.value,
    ],
    queryFn: () => {
      const params = new URLSearchParams();
      if (divisi.value) params.append("divisi", divisi.value);
      const qs = params.toString();
      return fetchGet(
        `Accounting/Mon/HutangJurnal/${bisnisUnit}/${periode.bulan}/${periode.tahun}` +
          (qs ? `?${qs}` : ""),
      );
    },
    keepPreviousData: true,
    staleTime: 30000,
    enabled: viewMode === VIEW_HUTANG,
  });

  // Ambil SELURUH baris yang cocok dengan filter aktif — bukan hanya halaman yang
  // sedang tampil.
  //
  // Tabel ini dipaginasi di SISI SERVER (page & limit dikirim ke API), jadi
  // `monitoringData.data` hanya berisi satu halaman. Export yang membacanya
  // menghasilkan PDF/Excel sebanyak `limit` baris saja — 10 baris dari 138,
  // tanpa tanda apa pun bahwa sisanya hilang. Justru itu yang paling berbahaya:
  // hasilnya terlihat sah dan mudah dikira laporan lengkap.
  //
  // Karena itu export meminta ulang datanya dengan limit = jumlah total, memakai
  // filter yang persis sama dengan yang sedang tampil.
  const ambilSemuaBaris = useCallback(async () => {
    const endpoint = `Accounting/Mon/PembayaranAP/${bisnisUnit}/${periode.bulan}/${periode.tahun}`;
    const params = new URLSearchParams();
    if (searchQuery && searchQuery.trim() !== "") params.append("search", searchQuery.trim());
    if (divisi.value) params.append("divisi", divisi.value);
    if (tanggal.mulai) params.append("tglMulai", tanggal.mulai);
    if (tanggal.akhir) params.append("tglAkhir", tanggal.akhir);
    params.append("page", "1");
    // totalRows berasal dari `count` respons terakhir; fallback 10000 menjaga
    // kalau count belum sempat termuat.
    params.append("limit", String(totalRows > 0 ? totalRows : 10000));
    const res = await fetchGet(`${endpoint}?${params.toString()}`);
    return res?.data ?? [];
  }, [bisnisUnit, periode, searchQuery, divisi.value, tanggal, totalRows]);

  // Nama file export — mengandung BU, periode, & divisi agar mudah dibedakan.
  const exportFileName = () =>
    `Monitoring_AP_${bisnisUnit}_${periode.bulan}_${periode.tahun}` +
    (divisi.value ? `_${divisi.value}` : "");

  // Export Excel — meng-export data yang SEDANG TERMUAT di tabel (gaya Buku Bank)
  // via downloadExcel (bundled, tanpa CDN). Filter divisi/periode sudah tercermin
  // di data karena berasal dari hasil query yang sama dengan tampilan.
  const handleDownloadExcel = useCallback(async () => {
    setIsExporting(true);
    const rows = await ambilSemuaBaris().catch(() => []);
    if (rows.length === 0) {
      setIsExporting(false);
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    try {
      downloadExcel({
        fileName: exportFileName(),
        sheet: "Monitoring AP",
        tablePayload: { header: EXPORT_HEADER, body: buildExportBody(rows) },
      });
      toast.success(`Berhasil export ${rows.length} data ke Excel`, {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      console.error("Export error:", error);
      toast.error(
        "Gagal export Excel: " + (error.message || "Unknown error"),
        { position: "top-center", autoClose: 3000 },
      );
    } finally {
      setIsExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambilSemuaBaris, bisnisUnit, periode, divisi]);

  // Export PDF — jsPDF landscape + autoTable, SELURUH baris sesuai filter + baris TOTAL.
  const handleDownloadPDF = useCallback(async () => {
    setIsExporting(true);
    const rows = await ambilSemuaBaris().catch(() => []);
    if (rows.length === 0) {
      setIsExporting(false);
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    try {
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text("MONITORING PEMBAYARAN AP", 14, 15);
      doc.setFontSize(10);
      // Rentang tanggal MENGGANTIKAN label periode saat aktif — menampilkan
      // "Periode: Januari 2026" pada PDF yang sebenarnya berisi 15 Jan–20 Mar
      // akan menyesatkan pembacanya.
      const labelPeriode =
        tanggal.mulai || tanggal.akhir
          ? `Tanggal: ${tanggal.mulai || "awal"} s/d ${tanggal.akhir || "akhir"}`
          : `Periode: ${periodeLabel}`;
      doc.text(
        `${labelPeriode} | BU: ${bisnisUnit} | Divisi: ${divisi.label} | ${rows.length} data`,
        14,
        22,
      );

      const body = buildExportBody(rows);
      autoTable(doc, {
        startY: 28,
        head: [EXPORT_HEADER],
        body,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: "bold" },
        didParseCell: (data) => {
          // Baris terakhir = TOTAL, ditebalkan.
          if (data.row.index === body.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [225, 232, 240];
          }
          // Kolom uang: Nominal PO (5), Nominal Bayar (6), Uang Muka (7),
          // Sisa Bayar (8) rata kanan.
          if (data.column.index >= 6 && data.column.index <= 9)
            data.cell.styles.halign = "right";
          if (data.column.index === 0) data.cell.styles.halign = "center";
        },
      });

      doc.save(`${exportFileName()}.pdf`);
      toast.success(`Berhasil export ${rows.length} data ke PDF`, {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      toast.error("Gagal export PDF: " + (error.message || "Unknown error"), {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsExporting(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ambilSemuaBaris, periodeLabel, bisnisUnit, periode, divisi]);

  // Export Excel tabel Hutang di Jurnal — meng-export baris yang SEDANG tampil
  // (sudah terfilter search), ditutup baris TOTAL Debet/Kredit.
  const handleDownloadExcelHutang = useCallback(() => {
    const rows = hutangFiltered;
    if (rows.length === 0) {
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    try {
      setIsExporting(true);
      downloadExcel({
        fileName: `Daftar_Hutang_${bisnisUnit}_${periode.bulan}_${periode.tahun}`,
        sheet: "Daftar Hutang",
        tablePayload: {
          header: EXPORT_HEADER_HUTANG,
          body: buildExportBodyHutang(rows),
        },
      });
      toast.success(`Berhasil export ${rows.length} data ke Excel`, {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      toast.error("Gagal export Excel: " + (error.message || "Unknown error"), {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsExporting(false);
    }
  }, [hutangFiltered, bisnisUnit, periode]);

  // Export PDF tabel Hutang di Jurnal — jsPDF landscape + autoTable + baris TOTAL.
  const handleDownloadPDFHutang = useCallback(() => {
    const rows = hutangFiltered;
    if (rows.length === 0) {
      toast.warn("Tidak ada data untuk di-export", {
        position: "top-center",
        autoClose: 2000,
      });
      return;
    }
    try {
      setIsExporting(true);
      const doc = new jsPDF({ orientation: "landscape" });
      doc.setFontSize(14);
      doc.text("DAFTAR HUTANG PO (BELUM LUNAS)", 14, 15);
      doc.setFontSize(10);
      doc.text(`Periode: ${periodeLabel} | BU: ${bisnisUnit}`, 14, 22);

      const body = buildExportBodyHutang(rows);
      autoTable(doc, {
        startY: 28,
        head: [EXPORT_HEADER_HUTANG],
        body,
        styles: { fontSize: 7, cellPadding: 2 },
        headStyles: { fillColor: [30, 58, 95], textColor: 255, fontStyle: "bold" },
        didParseCell: (data) => {
          if (data.row.index === body.length - 1) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [225, 232, 240];
          }
          // Kolom Total Hutang (3), Terbayar (4), Sisa (5) rata kanan; No (0) rata tengah.
          if (data.column.index >= 3) data.cell.styles.halign = "right";
          if (data.column.index === 0) data.cell.styles.halign = "center";
        },
      });

      doc.save(`Daftar_Hutang_${bisnisUnit}_${periode.bulan}_${periode.tahun}.pdf`);
      toast.success(`Berhasil export ${rows.length} data ke PDF`, {
        position: "top-center",
        autoClose: 2000,
      });
    } catch (error) {
      toast.error("Gagal export PDF: " + (error.message || "Unknown error"), {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsExporting(false);
    }
  }, [hutangFiltered, periodeLabel, bisnisUnit, periode]);


  // Handler untuk filter
  const handleFilter = useCallback(() => {
    const selectedDate = new Date(values.Bulan);
    const bulan = (selectedDate.getMonth() + 1).toString();
    const tahun = selectedDate.getFullYear().toString();

    setBisnisUnit(isSO ? SO_UNIT : values.BisnisUnitID);
    setPeriode({ bulan, tahun });
    setPage(1);
    setSearchInput("");
    setSearchQuery("");
    setIsSearching(false);
    debouncedSetSearchQuery.cancel();

    toast.success(`Menampilkan data periode Bulan ${bulan} Tahun ${tahun}`, {
      position: "top-center",
      autoClose: 1000,
    });
  }, [values, debouncedSetSearchQuery, isSO]);

  // Handler untuk pagination
  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Handler untuk change limit per page
  const handlePerRowsChange = useCallback((newLimit, page) => {
    setLimit(newLimit === -1 ? 999999 : newLimit);
    setPage(1);
  }, []);

  // Handler untuk refresh
  const handleRefresh = useCallback(() => {
    if (viewMode === VIEW_HUTANG) {
      hutangRefetch();
    } else {
      refetch();
    }
    toast.info("Memuat ulang data...", {
      position: "top-center",
      autoClose: 1000,
    });
  }, [refetch, hutangRefetch, viewMode]);

  // Format currency
  const formatCurrency = useCallback((amount) => {
    if (!amount && amount !== 0) return "Rp 0";
    try {
      return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
      }).format(amount);
    } catch {
      return amount?.toString() || "Rp 0";
    }
  }, []);

  // Format tanggal
  const formatDate = useCallback((dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString("id-ID", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch {
      return dateString;
    }
  }, []);

  // Navigasi ke detail voucher
  // Menyimpan pembayaran = MENERBITKAN JURNAL bertanggal itu. Backend menolak
  // dokumen yang belum disetujui, yang sudah punya TanggalBayar, atau yang sudah
  // punya baris Accounting — jadi klik ganda tak bisa melahirkan jurnal kembar.
  const handleSimpanBayar = useCallback(
    async (tanggal) => {
      setBayarBusy(true);
      try {
        // fetchPost memunculkan toast sukses/gagal sendiri dan MELEMPAR error
        // saat gagal — jadi tak perlu toast tambahan di sini.
        await fetchPost("Accounting/PembayaranAP/bayar", {
          ID: bayarModal.id,
          TanggalBayar: tanggal,
        });
        setBayarModal({ show: false, id: "", nominal: "" });
        refetch();
      } catch (_) {
        // Modal sengaja DIBIARKAN TERBUKA saat gagal. Penyebab tersering adalah
        // dokumen yang detail lawannya kosong atau tidak seimbang; menutup modal
        // membuat pengguna kehilangan tanggal yang baru saja dipilih.
      } finally {
        setBayarBusy(false);
      }
    },
    [bayarModal.id, refetch]
  );

  const handleViewVoucher = useCallback((voucherId, e) => {
    if (e) e.stopPropagation();
    if (!voucherId) return;
    const formattedId = voucherId.toString().replace(/\//g, "-");
    navigate(`/finance/detail/voucher-ap/${formattedId}`);
  }, [navigate]);

  // Cetak blanko — halaman blanko AP (bentuk sama dgn blanko Non PO).
  const handleViewBlanko = useCallback((voucherId, e) => {
    if (e) e.stopPropagation();
    if (!voucherId) {
      toast.error("No. Voucher tidak ditemukan");
      return;
    }
    const formattedId = voucherId.toString().replace(/\//g, "-");
    navigate(`/finance/report/blanco/pembayaran-ap/${formattedId}`);
  }, [navigate]);

  // Buka modal detail rincian hutang satu PO. Mengambil jurnal timbul + pembayaran
  // dari endpoint HutangPO/Detail. PO#### tak mengandung slash, jadi aman di path.
  const openHutangDetail = useCallback(async (row) => {
    setDetailPO(row);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const resp = await fetchGet(
        `Accounting/Mon/HutangPO/Detail/${encodeURIComponent(row.PurchaseOrderID)}`,
      );
      // fetchGet mengembalikan {status,message,data} (SuccessBuilder) → ambil .data.
      setDetailData(resp?.data ?? resp ?? { Jurnal: [], Pembayaran: [] });
    } catch (e) {
      toast.error("Gagal memuat detail: " + (e.message || "Unknown error"));
      setDetailData({ Jurnal: [], Pembayaran: [] });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const closeHutangDetail = useCallback(() => {
    setDetailPO(null);
    setDetailData(null);
  }, []);

  // Data preparation
  const tableData = monitoringData?.data || [];
  const totalRows = monitoringData?.count || 0;
  const isLoadingData = isLoading || isFetching;

  // ── Data tabel Hutang di Jurnal ──────────────────────────────
  const hutangRows = hutangResp?.data || [];
  const isLoadingHutang = hutangLoading || hutangFetching;

  // Filter client-side memakai kotak search yang sama (No. PO / Supplier / Keterangan).
  // searchInput dipakai langsung agar terasa instan.
  const hutangFiltered = useMemo(() => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return hutangRows;
    return hutangRows.filter((r) =>
      [r.PurchaseOrderID, r.DibayarKepada, r.Keterangan].some((v) =>
        (v || "").toString().toLowerCase().includes(q),
      ),
    );
  }, [hutangRows, searchInput]);

  // Total kolom (Total Hutang / Terbayar / Sisa) untuk baris ringkasan di bawah tabel.
  const hutangTotal = useMemo(
    () =>
      hutangFiltered.reduce(
        (acc, r) => {
          acc.totalHutang += parseFloat(r.TotalHutang) || 0;
          acc.terbayar += parseFloat(r.Terbayar) || 0;
          acc.sisa += parseFloat(r.Sisa) || 0;
          return acc;
        },
        { totalHutang: 0, terbayar: 0, sisa: 0 },
      ),
    [hutangFiltered],
  );

  // Tombol export — meng-export data yang sedang termuat di tabel (gaya Buku
  // Bank). Excel via downloadExcel (bundled, tanpa CDN), PDF via jsPDF.
  // Handler & kondisi disabled mengikuti tampilan aktif (pembayaran vs hutang).
  const isHutangView = viewMode === VIEW_HUTANG;
  const onExcel = isHutangView ? handleDownloadExcelHutang : handleDownloadExcel;
  const onPDF = isHutangView ? handleDownloadPDFHutang : handleDownloadPDF;
  const exportDisabled = isHutangView
    ? isExporting || isLoadingHutang || hutangFiltered.length === 0
    : isExporting || isLoadingData || totalRows === 0;
  const renderExportButtons = () => (
    <>
      <button
        type="button"
        onClick={onExcel}
        disabled={exportDisabled}
        title="Download data termuat ke Excel"
        className={`${BTN_BASE} bg-white text-green-700 border border-green-600 hover:bg-green-600 hover:text-white`}
      >
        {isExporting ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600" />
        ) : (
          <BsFileEarmarkExcelFill />
        )}
        Download Excel
      </button>

      <button
        type="button"
        onClick={onPDF}
        disabled={exportDisabled}
        title="Download data termuat ke PDF"
        className={`${BTN_BASE} bg-white text-red-600 border border-red-500 hover:bg-red-500 hover:text-white`}
      >
        {isExporting ? (
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-500" />
        ) : (
          <BsFileEarmarkPdfFill />
        )}
        Download PDF
      </button>
    </>
  );

  // Columns definition
  const columns = useMemo(
    () => [
      {
        name: "No",
        selector: (row, index) => (page - 1) * limit + index + 1,
        sortable: false,
        width: "70px",
        center: true,
      },
      {
        name: "No. Voucher",
        selector: (row) => row.PembayaranAPID,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <div className="text-xs font-mono whitespace-normal break-all">
            {row.PembayaranAPID || "-"}
          </div>
        ),
      },
      {
        name: "No. PO",
        selector: (row) => row.PurchaseOrderID,
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <div className="text-xs font-mono whitespace-nowrap">
            {row.PurchaseOrderID || "-"}
          </div>
        ),
      },
      {
        name: "Tanggal",
        selector: (row) => row.Tanggal,
        sortable: true,
        minWidth: "100px",
        cell: (row) => (
          <div className="text-xs whitespace-nowrap">
            {formatDate(row.Tanggal)}
          </div>
        ),
      },
      {
        name: "Keterangan",
        selector: (row) => row.Keterangan,
        sortable: true,
        minWidth: "200px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.Keterangan || "-"}
          </div>
        ),
      },
      {
        name: "Dibayar Kepada",
        selector: (row) => row.DibayarKepada,
        sortable: true,
        minWidth: "150px",
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words">
            {row.DibayarKepada || "-"}
          </div>
        ),
      },
      {
        name: "Nominal PO",
        selector: (row) => parseFloat(row.Nominal) || 0,
        sortable: true,
        minWidth: "150px",
        right: true,
        cell: (row) => (
          <div className="text-xs text-right whitespace-nowrap text-gray-600">
            {formatCurrency(parseFloat(row.Nominal) || 0)}
          </div>
        ),
      },
      {
        name: "Nominal Bayar",
        selector: (row) => row.NilaiBayar,
        sortable: true,
        minWidth: "150px",
        right: true,
        cell: (row) => (
          <div className="text-xs font-medium text-right whitespace-nowrap">
            {formatCurrency(row.NilaiBayar)}
          </div>
        ),
      },
      {
        name: "Uang Muka",
        // Porsi termin yang dilunasi dari uang muka (downpayment). 0 bila tak ada.
        selector: (row) => parseFloat(row.DownpaymentAmount) || 0,
        sortable: true,
        minWidth: "150px",
        right: true,
        cell: (row) => {
          const dp = parseFloat(row.DownpaymentAmount) || 0;
          return (
            <div
              className={`text-xs font-medium text-right whitespace-nowrap ${
                dp > 0 ? "text-blue-600" : "text-gray-400"
              }`}
            >
              {formatCurrency(dp)}
            </div>
          );
        },
      },
      {
        name: "Sisa Bayar",
        // Rumusnya di hitungSisaBayar (satu sumber, dipakai tabel & export):
        // belum Approved → Nominal Bayar utuh; sudah Approved → selisihnya.
        selector: (row) => hitungSisaBayar(row),
        sortable: true,
        minWidth: "150px",
        right: true,
        cell: (row) => {
          const sisa = hitungSisaBayar(row);
          return (
            <div
              className={`text-xs font-medium text-right whitespace-nowrap ${
                sisa > 0 ? "text-red-600" : "text-gray-500"
              }`}
            >
              {formatCurrency(sisa)}
            </div>
          );
        },
      },
      {
        name: "Status",
        selector: (row) => row.Status,
        sortable: true,
        minWidth: "150px",
        cell: (row) => {
          const st = mapStatus(row.Status);
          // "Terbayar" bukan angka status tersendiri, melainkan turunan dari
          // TanggalBayar — lihat catatan pada kolom itu di backend.
          if (st.isApproved && row.TanggalBayar) {
            return (
              <span className="px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap bg-blue-100 text-blue-700">
                Terbayar {row.TanggalBayar}
              </span>
            );
          }
          return <span className={statusClass(st)}>{st.value}</span>;
        },
      },
      {
        name: "Aksi",
        cell: (row) => {
          const voucherId = row.PembayaranAPID || "";
          // Blanko hanya untuk pengajuan yang sudah disetujui — mengikuti pola
          // monitoring Non PO. Yang masih menunggu/ditolak belum boleh dicetak.
          const bolehCetak = mapStatus(row.Status).isApproved;
          // Bayar hanya untuk yang sudah disetujui TAPI belum dibayar. Setelah
          // dibayar tombolnya hilang — penjaga sesungguhnya ada di backend, ini
          // supaya tak ada yang mencoba lalu ditolak.
          const bolehBayar = bolehCetak && !row.TanggalBayar;

          return (
            <div className="flex flex-wrap gap-2">
              {bolehBayar && (
                <button
                  onClick={() =>
                    setBayarModal({ show: true, id: voucherId, nominal: row.NilaiBayar, tanggalDokumen: row.Tanggal })
                  }
                  className="border text-white bg-blue-600 hover:bg-white hover:border-blue-600 hover:text-blue-600 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                  title="Catat pembayaran — jurnal terbit pada tanggal yang dipilih"
                >
                  Bayar
                </button>
              )}
              <button
                onClick={(e) => handleViewVoucher(voucherId, e)}
                className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 focus:ring-4 focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                title="Lihat Voucher AP"
              >
                Lihat
              </button>
              {bolehCetak && (
                <button
                  onClick={(e) => handleViewBlanko(voucherId, e)}
                  className="border text-white bg-green-600 hover:bg-white hover:border-green-600 hover:text-green-600 focus:ring-4 focus:ring-green-300 font-medium rounded-lg text-sm px-3 py-1.5 focus:outline-none transition-colors duration-200"
                  title="Cetak Blanko"
                >
                  Blanko
                </button>
              )}
            </div>
          );
        },
        ignoreRowClick: true,
        allowOverflow: true,
        minWidth: "180px",
      },
    ],
    [page, limit, formatDate, formatCurrency, handleViewVoucher, handleViewBlanko, setBayarModal]
  );

  // Kolom tabel Daftar Hutang — satu baris = satu PO (Hutang PO) yang belum lunas.
  const hutangColumns = useMemo(
    () => [
      {
        name: "No",
        selector: (row, index) => index + 1,
        sortable: false,
        width: "70px",
        center: true,
      },
      {
        name: "No. PO",
        selector: (row) => row.PurchaseOrderID,
        sortable: true,
        minWidth: "120px",
        cell: (row) => (
          <div className="text-xs font-mono whitespace-nowrap">
            {row.PurchaseOrderID || "-"}
          </div>
        ),
      },
      {
        name: "Supplier / Keterangan",
        selector: (row) => row.DibayarKepada || row.Keterangan,
        sortable: true,
        minWidth: "260px",
        grow: 2,
        cell: (row) => (
          <div className="text-xs whitespace-normal break-words py-1">
            <div className="font-medium text-gray-800">
              {row.DibayarKepada || "-"}
            </div>
            {row.Keterangan && (
              <div className="text-gray-500">{row.Keterangan}</div>
            )}
          </div>
        ),
      },
      {
        name: "Total Hutang",
        selector: (row) => parseFloat(row.TotalHutang) || 0,
        sortable: true,
        minWidth: "140px",
        right: true,
        cell: (row) => (
          <div className="text-xs font-medium text-right whitespace-nowrap">
            {formatCurrency(parseFloat(row.TotalHutang) || 0)}
          </div>
        ),
      },
      {
        name: "Terbayar",
        selector: (row) => parseFloat(row.Terbayar) || 0,
        sortable: true,
        minWidth: "140px",
        right: true,
        cell: (row) => (
          <div className="text-xs text-right whitespace-nowrap text-gray-600">
            {formatCurrency(parseFloat(row.Terbayar) || 0)}
          </div>
        ),
      },
      {
        name: "Sisa (Belum Lunas)",
        selector: (row) => parseFloat(row.Sisa) || 0,
        sortable: true,
        minWidth: "150px",
        right: true,
        cell: (row) => (
          <div className="text-xs font-semibold text-right whitespace-nowrap text-red-600">
            {formatCurrency(parseFloat(row.Sisa) || 0)}
          </div>
        ),
      },
      {
        name: "Aksi",
        minWidth: "110px",
        ignoreRowClick: true,
        allowOverflow: true,
        cell: (row) => (
          <button
            onClick={() => openHutangDetail(row)}
            className="border text-white bg-main-100 hover:bg-white hover:border-main-100 hover:text-main-100 font-medium rounded-lg text-xs px-3 py-1.5 focus:outline-none transition-colors"
            title="Lihat rincian hutang & pembayaran PO ini"
          >
            Detail
          </button>
        ),
      },
    ],
    [formatCurrency, openHutangDetail],
  );

  // Custom styles untuk DataTable
  const customStyles = useMemo(
    () => ({
      head: {
        style: {
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
          fontSize: "12px",
          color: "#374151",
          textTransform: "uppercase",
        },
      },
      headCells: {
        style: {
          paddingLeft: "12px",
          paddingRight: "12px",
          whiteSpace: "nowrap",
          borderBottom: "2px solid #e5e7eb",
          backgroundColor: "#f8f9fa",
          fontWeight: "bold",
          fontSize: "12px",
        },
      },
      cells: {
        style: {
          fontSize: "12px",
          paddingLeft: "12px",
          paddingRight: "12px",
          whiteSpace: "normal",
          wordBreak: "break-word",
          minHeight: "50px",
          verticalAlign: "top",
          borderBottom: "1px solid #f3f4f6",
        },
      },
      rows: {
        style: {
          minHeight: "50px",
          "&:nth-of-type(even)": {
            backgroundColor: "#f9fafb",
          },
          "&:hover": {
            backgroundColor: "#f3f4f6",
            transition: "background-color 0.2s ease",
          },
        },
      },
      pagination: {
        style: {
          fontSize: "12px",
          borderTop: "1px solid #e5e7eb",
          paddingTop: "12px",
          paddingBottom: "12px",
        },
      },
    }),
    [],
  );

  // Tampilkan error jika ada
  if (error) {
    return (
      <div className="md:ml-64">
        <div className="px-3 md:px-8 h-auto mt-32">
          <div className="container mx-auto max-w-full">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              Error: {error.message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="md:ml-64">
      <ModalBayar
        show={bayarModal.show}
        dokumenId={bayarModal.id}
        nominal={bayarModal.nominal}
        tanggalDokumen={bayarModal.tanggalDokumen}
        busy={bayarBusy}
        onSimpan={handleSimpanBayar}
        onBatal={() => setBayarModal({ show: false, id: "", nominal: "" })}
      />
      <div
        style={{ background: Colors.primary }}
        className="pt-14 pb-16 px-3 md:px-8 h-auto"
      >
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4"></div>
        </div>
      </div>

      <div className="px-3 md:px-8 h-auto -mt-32">
        <div className="container mx-auto max-w-full">
          <div className="grid grid-cols-1 px-4">
            <Card className="shadow-lg">
              <div className="p-6">
                {/* Filter Section */}
                <div className="mb-6">
                  <div className="flex flex-wrap gap-4 items-end">
                    {/* Bisnis Unit */}
                    <div className="w-full md:w-44">
                      <HrdInputGenerator
                        type="BisnisUnit"
                        name="BisnisUnitID"
                        title="Bisnis Unit"
                        value={values}
                        required={true}
                        onChange={customHandler}
                        touched={touched}
                        errors={errors}
                        size="full"
                        disabled={lockBU}
                      />
                      {isSO && (
                        <p className="mt-1 text-xs italic text-gray-500">
                          Terkunci ke unit Anda (SO).
                        </p>
                      )}
                    </div>

                    {/* Periode */}
                    <div className="w-full md:w-44">
                      <HrdInputGenerator
                        type="bulan"
                        name="Bulan"
                        title="Periode"
                        value={values}
                        required={true}
                        onChange={customHandler}
                        touched={touched}
                        errors={errors}
                        size="full"
                      />
                    </div>

                    {/* Divisi — `px-3 mb-6` WAJIB: HrdInputGenerator (size="full")
                        membungkus dirinya dgn kelas itu, dan baris ini items-end
                        (rata bawah). Tanpa mb-6, kolom Divisi melorot 24px dari
                        Bisnis Unit & Periode. */}
                    <div className="w-full md:w-44 px-3 mb-6">
                      {/* Kelas label disamakan PERSIS dgn HrdInputGenerator
                          (lihat komponennya) supaya sebaris dgn Bisnis Unit &
                          Periode — beda kelas sedikit saja bikin kolom melorot. */}
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Divisi
                      </label>
                      <Select
                        value={divisi}
                        options={divisiOptions}
                        onChange={(opt) => {
                          setDivisi(opt ?? SEMUA_DIVISI);
                          setPage(1);
                        }}
                        className="text-sm"
                        classNamePrefix="divisi"
                        placeholder="Semua Divisi"
                        styles={{
                          control: (base) => ({ ...base, minHeight: "40px" }),
                        }}
                      />
                    </div>

                    {/* Rentang tanggal. `px-3 mb-6` mengikuti pola kolom Divisi di
                        atas — HrdInputGenerator (size="full") membungkus dirinya
                        dgn kelas itu, dan baris ini items-end, jadi tanpa mb-6
                        kolomnya melorot 24px. */}
                    <div className="w-full md:w-auto px-3 mb-6">
                      <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                        Rentang Tanggal
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="date"
                          value={tanggal.mulai}
                          onChange={(e) => {
                            setTanggal((p) => ({ ...p, mulai: e.target.value }));
                            setPage(1);
                          }}
                          className="appearance-none text-sm text-gray-700 border border-gray-500 rounded px-3 leading-tight outline-none bg-white h-10"
                        />
                        <span className="text-gray-400 text-sm">s/d</span>
                        <input
                          type="date"
                          value={tanggal.akhir}
                          onChange={(e) => {
                            setTanggal((p) => ({ ...p, akhir: e.target.value }));
                            setPage(1);
                          }}
                          className="appearance-none text-sm text-gray-700 border border-gray-500 rounded px-3 leading-tight outline-none bg-white h-10"
                        />
                        {(tanggal.mulai || tanggal.akhir) && (
                          <button
                            type="button"
                            onClick={() => {
                              setTanggal({ mulai: "", akhir: "" });
                              setPage(1);
                            }}
                            className="text-xs text-gray-600 underline whitespace-nowrap"
                            title="Kembali memakai filter Periode"
                          >
                            reset
                          </button>
                        )}
                      </div>
                      {/* TIDAK ADA teks keterangan di bawah input.
                          Baris filter ini `items-end` (rata bawah), jadi apa pun
                          yang menempel di bawah kotak input akan MENGANGKAT kolom
                          ini terhadap Bisnis Unit / Periode / Divisi — yang
                          semuanya berakhir tepat di kotak input. Keterangan
                          "Periode diabaikan" dipindah ke panel info biru di bawah
                          baris filter. */}
                    </div>

                    {/* Tombol aksi — sebaris dgn field filter. `mb-6` menyamakan
                        margin bawah field (HrdInputGenerator size="full") supaya
                        tombol sejajar dgn KOTAK INPUT-nya, bukan melorot 24px ke
                        dasar baris (baris ini items-end / rata bawah). */}
                    <div className="flex flex-wrap items-center gap-2 mb-6">
                      <button
                        type="button"
                        onClick={handleFilter}
                        className={`${BTN_BASE} bg-blue-600 text-white hover:bg-blue-700`}
                      >
                        <BiFilter />
                        Buka Data
                      </button>

                      <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={isLoadingData}
                        className={`${BTN_BASE} bg-gray-500 text-white hover:bg-gray-600`}
                      >
                        <BiRefresh className={isFetching ? "animate-spin" : ""} />
                        Refresh
                      </button>
                    </div>

                    {/* Info Loading */}
                    {isFetching && !isFirstLoad && (
                      <div className="flex items-center">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
                        <span className="ml-2 text-sm text-gray-500">
                          Memuat data...
                        </span>
                      </div>
                    )}

                    {/* Info Total Data */}
                    {totalRows > 0 && (
                      <div className="text-sm text-gray-600">
                        Total: {totalRows} data
                        {searchQuery && ` (hasil pencarian "${searchQuery}")`}
                      </div>
                    )}
                  </div>
                </div>

                {/* Pilihan tampilan (radio) — tabel utama Pembayaran AP atau
                    tabel seluruh hutang yang tercatat di jurnal. Keduanya memakai
                    filter Bisnis Unit + Periode yang sama di atas. */}
                <div className="mb-4 flex flex-wrap items-center gap-4">
                  <span className="text-xs font-bold text-gray-600 uppercase">
                    Tampilkan:
                  </span>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="viewMode"
                      value={VIEW_PEMBAYARAN}
                      checked={viewMode === VIEW_PEMBAYARAN}
                      onChange={() => setViewMode(VIEW_PEMBAYARAN)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Pembayaran AP</span>
                  </label>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="viewMode"
                      value={VIEW_HUTANG}
                      checked={viewMode === VIEW_HUTANG}
                      onChange={() => setViewMode(VIEW_HUTANG)}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm text-gray-700">Daftar Hutang</span>
                  </label>
                </div>

                {/* Info Periode yang Ditampilkan */}
                <div className="mb-4 px-4 py-2 bg-blue-50 border rounded-md">
                  <p className="text-sm text-blue-700">
                    {tanggal.mulai || tanggal.akhir ? (
                      <>
                        Menampilkan tanggal:{" "}
                        <strong>
                          {tanggal.mulai || "awal"} s/d {tanggal.akhir || "akhir"}
                        </strong>{" "}
                        <span className="italic">(filter Periode diabaikan)</span>{" "}
                      </>
                    ) : (
                      <>
                        Menampilkan periode:{" "}
                        <strong>
                          Bulan {periode.bulan} Tahun {periode.tahun}
                        </strong>{" "}
                      </>
                    )}
                    | Bisnis Unit: <strong>{bisnisUnit}</strong>
                    {viewMode === VIEW_PEMBAYARAN && searchQuery && (
                      <span className="ml-2">
                        | Pencarian: <strong>"{searchQuery}"</strong>
                      </span>
                    )}
                  </p>
                </div>

                {/* Search Bar */}
                <div className="mb-4">
                  <div className="relative w-full md:w-96">
                    <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      placeholder={
                        viewMode === VIEW_HUTANG
                          ? "Cari No. PO, Supplier, Keterangan..."
                          : "Cari No. Voucher, No. PO, Keterangan, Dibayar Kepada..."
                      }
                      value={searchInput}
                      onChange={
                        viewMode === VIEW_HUTANG
                          ? (e) => setSearchInput(e.target.value)
                          : handleSearchChange
                      }
                      className="pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
                    />
                    {searchInput && (
                      <button
                        onClick={handleClearSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear search"
                      >
                        ✕
                      </button>
                    )}
                    
                    {viewMode === VIEW_PEMBAYARAN && isSearching && (
                      <div className="absolute right-8 top-1/2 transform -translate-y-1/2">
                        <div className="flex items-center space-x-1">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                        </div>
                      </div>
                    )}
                  </div>

                  {viewMode === VIEW_PEMBAYARAN &&
                    searchInput !== searchQuery &&
                    searchInput && (
                      <p className="text-xs text-blue-500 mt-1 flex items-center">
                        <span className="animate-pulse mr-1">●</span>
                        Mengetik "{searchInput}"...
                      </p>
                    )}

                  {viewMode === VIEW_PEMBAYARAN && searchQuery && !isSearching && (
                    <p className="text-xs text-green-600 mt-1 flex items-center">
                      <span className="mr-1">✓</span>
                      Menampilkan hasil untuk "{searchQuery}"
                      {/^(po)?\d{3,}$/i.test(searchQuery.trim()) && " — nomor PO dicari di semua periode"}
                    </p>
                  )}

                  {viewMode === VIEW_HUTANG && searchInput && (
                    <p className="text-xs text-green-600 mt-1 flex items-center">
                      <span className="mr-1">✓</span>
                      {hutangFiltered.length} PO cocok dengan "{searchInput}"
                    </p>
                  )}
                </div>

                {/* Toolbar export + judul laporan — gaya Buku Bank. Tombol
                    meng-export data yang SEDANG termuat di tabel. */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-2">
                  <div className="flex flex-row gap-2">{renderExportButtons()}</div>
                </div>
                <div className="text-center mb-3">
                  <h2 className="text-lg font-bold text-gray-800 uppercase">
                    {viewMode === VIEW_HUTANG
                      ? "Daftar Hutang PO (Belum Lunas)"
                      : "Monitoring Pembayaran AP"}
                  </h2>
                  <p className="text-sm text-gray-600">
                    Periode: {periodeLabel} | Bisnis Unit: {bisnisUnit}
                    {divisi.value ? ` | Divisi: ${divisi.label}` : ""}
                  </p>
                </div>

                {/* ── Tabel HUTANG di Jurnal ──────────────────────── */}
                {viewMode === VIEW_HUTANG ? (
                  hutangError ? (
                    <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
                      Error: {hutangError.message}
                    </div>
                  ) : (
                    <>
                      <div className="overflow-x-auto border rounded-lg shadow-sm">
                        <DataTable
                          columns={hutangColumns}
                          data={hutangFiltered}
                          customStyles={customStyles}
                          pagination
                          paginationRowsPerPageOptions={[10, 25, 50, 100]}
                          paginationPerPage={25}
                          highlightOnHover
                          striped
                          dense
                          progressPending={isLoadingHutang}
                          progressComponent={
                            <div className="flex flex-col justify-center items-center py-20">
                              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                              <span className="text-gray-600">Memuat data...</span>
                            </div>
                          }
                          noDataComponent={
                            <div className="text-center py-8">
                              <div className="text-gray-500 mb-2">
                                {searchInput
                                  ? `Tidak ada PO yang cocok dengan "${searchInput}"`
                                  : "Tidak ada hutang PO yang belum lunas pada periode ini."}
                              </div>
                            </div>
                          }
                          persistTableHead
                          fixedHeader
                          fixedHeaderScrollHeight="calc(100vh - 400px)"
                          responsive
                        />
                      </div>

                      {/* Ringkasan Total Hutang / Terbayar / Sisa tabel Daftar Hutang */}
                      {hutangFiltered.length > 0 && (
                        <div className="mt-4 text-sm text-gray-700">
                          <div className="flex flex-wrap justify-between items-center gap-2">
                            <div>
                              {hutangFiltered.length} PO belum lunas
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                              <span className="px-3 py-1 bg-gray-50 text-gray-700 rounded-full text-xs">
                                Total Hutang: {formatCurrency(hutangTotal.totalHutang)}
                              </span>
                              <span className="px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-xs">
                                Terbayar: {formatCurrency(hutangTotal.terbayar)}
                              </span>
                              <span className="px-3 py-1 bg-red-50 text-red-700 rounded-full text-xs font-semibold">
                                Sisa: {formatCurrency(hutangTotal.sisa)}
                              </span>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )
                ) : /* Table Section */
                isLoadingData && isFirstLoad ? (
                  <div className="w-full">
                    <Skeltons />
                  </div>
                ) : (
                  <>
                    <div className="overflow-x-auto border rounded-lg shadow-sm">
                      <DataTable
                        columns={columns}
                        data={tableData}
                        customStyles={customStyles}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        onChangePage={handlePageChange}
                        onChangeRowsPerPage={handlePerRowsChange}
                        paginationRowsPerPageOptions={[10, 25, 50, 100]}
                        paginationPerPage={limit}
                        paginationDefaultPage={page}
                        paginationResetDefaultPage={page === 1}
                        highlightOnHover
                        striped
                        dense
                        progressPending={isLoadingData}
                        progressComponent={
                          <div className="flex flex-col justify-center items-center py-20">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mb-4"></div>
                            <span className="text-gray-600">
                              {searchQuery ? `Mencari "${searchQuery}"...` : "Memuat data..."}
                            </span>
                          </div>
                        }
                        noDataComponent={
                          <div className="text-center py-8">
                            <div className="text-gray-500 mb-2">
                              {searchQuery
                                ? `Tidak ada data yang cocok dengan pencarian "${searchQuery}"`
                                : totalRows === 0
                                  ? "Tidak ada data. Klik 'Buka Data' untuk memuat."
                                  : "Tidak ada data yang ditemukan"}
                            </div>
                            {searchQuery && (
                              <button
                                onClick={handleClearSearch}
                                className="text-main-100 hover:text-main-200 font-medium text-sm"
                              >
                                Tampilkan semua data
                              </button>
                            )}
                          </div>
                        }
                        persistTableHead
                        fixedHeader
                        fixedHeaderScrollHeight="calc(100vh - 400px)"
                        responsive
                      />
                    </div>

                    {/* Info tambahan */}
                    {totalRows > 0 && (
                      <div className="mt-4 text-sm text-gray-600">
                        <div className="flex justify-between items-center">
                          <div>
                            Menampilkan halaman {page} dari{" "}
                            {Math.ceil(totalRows / limit)} halaman
                            {searchQuery &&
                              ` (${totalRows} data ditemukan untuk "${searchQuery}")`}
                          </div>
                          <div className="flex items-center space-x-2">
                            <span className="px-3 py-1 bg-gray-50 text-gray-700 rounded-full text-xs">
                              Periode: Bulan {periode.bulan} Tahun{" "}
                              {periode.tahun}
                            </span>
                            <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs">
                              {bisnisUnit}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* ── Modal Detail Rincian Hutang PO ─────────────────────── */}
      {detailPO && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={closeHutangDetail}
        >
          <div
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header modal */}
            <div className="flex items-start justify-between border-b px-6 py-4">
              <div>
                <h3 className="text-lg font-bold text-gray-800">
                  Detail Hutang — {detailPO.PurchaseOrderID}
                </h3>
                <p className="text-sm text-gray-600">
                  {detailPO.DibayarKepada || detailPO.Keterangan || "-"}
                </p>
                <div className="mt-1 flex flex-wrap gap-2 text-xs">
                  <span className="px-2 py-0.5 bg-gray-100 rounded-full">
                    Total Hutang:{" "}
                    {formatCurrency(parseFloat(detailPO.TotalHutang) || 0)}
                  </span>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full">
                    Terbayar: {formatCurrency(parseFloat(detailPO.Terbayar) || 0)}
                  </span>
                  <span className="px-2 py-0.5 bg-red-50 text-red-700 rounded-full font-semibold">
                    Sisa: {formatCurrency(parseFloat(detailPO.Sisa) || 0)}
                  </span>
                </div>
              </div>
              <button
                onClick={closeHutangDetail}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                title="Tutup"
              >
                ×
              </button>
            </div>

            {/* Isi modal */}
            <div className="overflow-y-auto px-6 py-4">
              {detailLoading ? (
                <div className="flex flex-col items-center py-16">
                  <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500 mb-3"></div>
                  <span className="text-gray-600 text-sm">Memuat rincian...</span>
                </div>
              ) : (
                <>
                  {/* Bagian 1: Jurnal hutang timbul */}
                  <h4 className="text-sm font-bold text-gray-700 uppercase mb-2">
                    Rincian Hutang (Jurnal Penerimaan / PO Jasa)
                  </h4>
                  <div className="overflow-x-auto border rounded mb-6">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-3 py-2 text-left">Tanggal</th>
                          <th className="px-3 py-2 text-left">No. Jurnal</th>
                          <th className="px-3 py-2 text-left">Akun</th>
                          <th className="px-3 py-2 text-left">Keterangan</th>
                          <th className="px-3 py-2 text-right">Kredit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detailData?.Jurnal || []).map((j, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatDate(j.Tanggal)}
                            </td>
                            <td className="px-3 py-2 font-mono whitespace-nowrap">
                              {j.AccountingID}
                            </td>
                            <td className="px-3 py-2">
                              <span className="font-mono">{j.DetailAccountID}</span>{" "}
                              <span className="text-gray-500">{j.DetailAccount}</span>
                            </td>
                            <td className="px-3 py-2">{j.Keterangan}</td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {formatCurrency(parseFloat(j.Kredit) || 0)}
                            </td>
                          </tr>
                        ))}
                        {(detailData?.Jurnal || []).length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                              Tidak ada baris jurnal hutang.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {(detailData?.Jurnal || []).length > 0 && (
                        <tfoot className="bg-gray-50 font-semibold">
                          <tr className="border-t">
                            <td colSpan={4} className="px-3 py-2 text-right">
                              Total Hutang
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {formatCurrency(
                                (detailData?.Jurnal || []).reduce(
                                  (a, j) => a + (parseFloat(j.Kredit) || 0),
                                  0,
                                ),
                              )}
                            </td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>

                  {/* Bagian 2: Riwayat pembayaran AP */}
                  <h4 className="text-sm font-bold text-gray-700 uppercase mb-2">
                    Riwayat Pembayaran AP
                  </h4>
                  <div className="overflow-x-auto border rounded">
                    <table className="min-w-full text-xs">
                      <thead className="bg-gray-50 text-gray-600">
                        <tr>
                          <th className="px-3 py-2 text-left">No. Voucher</th>
                          <th className="px-3 py-2 text-left">Tanggal</th>
                          <th className="px-3 py-2 text-right">Nominal PO</th>
                          <th className="px-3 py-2 text-right">Nilai Bayar</th>
                          <th className="px-3 py-2 text-center">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(detailData?.Pembayaran || []).map((p, i) => {
                          const st = mapStatus(p.Status);
                          return (
                            <tr key={i} className="border-t">
                              <td className="px-3 py-2 font-mono whitespace-nowrap">
                                {p.PembayaranAPID}
                              </td>
                              <td className="px-3 py-2 whitespace-nowrap">
                                {formatDate(p.Tanggal)}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {formatCurrency(parseFloat(p.Nominal) || 0)}
                              </td>
                              <td className="px-3 py-2 text-right whitespace-nowrap">
                                {formatCurrency(parseFloat(p.NilaiBayar) || 0)}
                              </td>
                              <td className="px-3 py-2 text-center">
                                <span className={statusClass(st)}>{st.value}</span>
                              </td>
                            </tr>
                          );
                        })}
                        {(detailData?.Pembayaran || []).length === 0 && (
                          <tr>
                            <td colSpan={5} className="px-3 py-4 text-center text-gray-500">
                              Belum ada pembayaran AP untuk PO ini.
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {(detailData?.Pembayaran || []).length > 0 && (
                        <tfoot className="bg-gray-50 font-semibold">
                          <tr className="border-t">
                            <td colSpan={3} className="px-3 py-2 text-right">
                              Terbayar (Approved)
                            </td>
                            <td className="px-3 py-2 text-right whitespace-nowrap">
                              {formatCurrency(
                                (detailData?.Pembayaran || [])
                                  .filter((p) => mapStatus(p.Status).isApproved)
                                  .reduce((a, p) => a + (parseFloat(p.NilaiBayar) || 0), 0),
                              )}
                            </td>
                            <td></td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </>
              )}
            </div>

            {/* Footer modal */}
            <div className="border-t px-6 py-3 text-right">
              <button
                onClick={closeHutangDetail}
                className="px-4 py-2 text-sm font-medium rounded-lg bg-gray-500 text-white hover:bg-gray-600"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MonitoringAP;